package gr.tech.GRAttestation.styles.store

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import gr.tech.GRAttestation.styles.grstyles.Theme
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent

// ── Portal-style inputs (matching the HTML design reference) ──────────────────

val PortalInputLabelStyle = CssStyle {
    base {
        Modifier
            .fontSize(11.px)
            .fontWeight(FontWeight.SemiBold)
            .textTransform(TextTransform.Uppercase)
            .letterSpacing(0.08.em)
            .color(rgb(74, 112, 112))
            .margin(bottom = 5.px)
    }
}

val PortalInputStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .height(45.px)
            .padding(9.px, 13.px)
            .border(1.5.px, LineStyle.Solid, rgb(184, 208, 198))
            .borderRadius(8.px)
            .fontSize(14.px)
            .backgroundColor(rgb(252, 254, 252))
    }

    cssRule(":focus") {
        Modifier
            .border(1.5.px, LineStyle.Solid, rgb(42, 124, 124))
            .boxShadow(
                offsetX = 0.px, offsetY = 0.px,
                blurRadius = 0.px, spreadRadius = 3.px,
                color = rgba(42, 124, 124, 0.12)
            )
    }
}

val PortalSelectStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .height(45.px)
            .padding(9.px, 13.px)
            .border(1.5.px, LineStyle.Solid, rgb(184, 208, 198))
            .borderRadius(8.px)
            .fontSize(14.px)
            .backgroundColor(rgb(252, 254, 252))
            .cursor(Cursor.Pointer)
    }

    cssRule(":focus") {
        Modifier
            .border(1.5.px, LineStyle.Solid, rgb(42, 124, 124))
            .boxShadow(
                offsetX = 0.px, offsetY = 0.px,
                blurRadius = 0.px, spreadRadius = 3.px,
                color = rgba(42, 124, 124, 0.12)
            )
    }
}

val PortalTextStyle = CssStyle {
    base {
        Modifier
            .fontSize(15.px)
            .fontWeight(700)
            .color(Theme.DarkGreen.rgb)
            .margin(0.px)
    }
}

val PortalSmallTextStyle = CssStyle {
    base {
        Modifier
        Modifier
            .fontSize(12.px)
            .color(rgb(106, 144, 144))
            .margin(top = 3.px, bottom = 0.px, leftRight = 0.px)
    }
}

// Store Portal Navbar Styles
val StoreNavbarStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .background(Theme.DarkGreen.rgb)
            .borderBottom(3.px, LineStyle.Solid, rgb(42, 124, 124))
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(50)
    }
}

val StoreNavbarContainerStyle = CssStyle {
    base {
        Modifier
            .maxWidth(900.px)
            .margin(leftRight = autoLength, topBottom = 0.px)
            .padding(14.px, 24.px)
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.SpaceBetween)
    }
}

val StoreNavbarLogoStyle = CssStyle {
    base {
        Modifier
            .maxHeight(40.px)
            .userSelect(UserSelect.None)
    }
}

val StoreNavbarStoreInfoStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(14.px)
    }
}

val StoreNavbarStoreNameStyle = CssStyle {
    base {
        Modifier
            .fontSize(12.px)
            .color(rgb(138, 188, 188))
    }
}

val StoreNavbarButtonStyle = CssStyle {
    base {
        Modifier
            .background(Colors.Transparent)
            .color(rgb(138, 188, 188))
            .border(1.5.px, LineStyle.Solid, rgb(58, 106, 106))
            .borderRadius(10.px)
            .padding(6.px, 14.px)
            .fontSize(11.px)
            .fontWeight(600)
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
            .transition(com.varabyte.kobweb.compose.css.Transition.of("all", 0.2.s))
    }

    cssRule(":hover") {
        Modifier
            .background(rgba(138, 188, 188, 0.1))
            .border(1.5.px, LineStyle.Solid, rgb(138, 188, 188))
    }
}

// Store Info Banner (top card beneath the navbar)
val StoreInfoBannerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, rgb(208, 224, 218))
            .borderRadius(14.px)
            .boxShadow(
                offsetX = 0.px, offsetY = 1.px,
                blurRadius = 3.px, spreadRadius = 0.px,
                color = rgba(0, 0, 0, 0.06)
            )
    }
}

val StoreInfoBannerInnerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(16.px, 26.px)
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
            .flexWrap(FlexWrap.Wrap)
            .gap(12.px)
    }
}

val StoreInfoNameStyle = CssStyle {
    base {
        Modifier
            .fontSize(15.px)
            .fontWeight(700)
            .color(Theme.DarkGreen.rgb)
    }
}

val StoreInfoAddressStyle = CssStyle {
    base {
        Modifier
            .fontSize(12.px)
            .color(rgb(106, 144, 144))
            .margin(top = 2.px)
    }
}

val StoreInfoPartnerStyle = CssStyle {
    base {
        Modifier
            .fontSize(11.px)
            .color(rgb(106, 144, 144))
            .textAlign(TextAlign.Right)
    }
}

// Dashboard Tab Bar
val DashboardTabBarStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .gap(4.px)
            .margin(bottom = 0.px)
    }
}

val DashboardTabButtonActiveStyle = CssStyle {
    base {
        Modifier
            .background(Theme.DarkGreen.rgb)
            .color(Colors.White)
            .border(0.px)
            .borderRadius(8.px, 8.px, 0.px, 0.px)
            .padding(10.px, 20.px)
            .fontSize(13.px)
            .fontWeight(600)
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
    }
}

val DashboardTabButtonInactiveStyle = CssStyle {
    base {
        Modifier
            .background(Colors.Transparent)
            .color(rgb(106, 144, 144))
            .border(0.px)
            .borderRadius(8.px, 8.px, 0.px, 0.px)
            .padding(10.px, 20.px)
            .fontSize(13.px)
            .fontWeight(500)
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
            .transition(com.varabyte.kobweb.compose.css.Transition.of("all", 0.15.s))
    }

    cssRule(":hover") {
        Modifier
            .color(Theme.DarkGreen.rgb)
            .background(rgba(2, 67, 66, 0.05))
    }
}

// Dashboard Tab Card
val DashboardTabCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, rgb(208, 224, 218))
            .borderRadius(0.px, 8.px, 8.px, 8.px)
            .boxShadow(
                offsetX = 0.px, offsetY = 1.px,
                blurRadius = 3.px, spreadRadius = 0.px,
                color = rgba(0, 0, 0, 0.06)
            )
    }
}

val DashboardTabCardHeaderStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .background(rgb(238, 246, 242))
            .padding(18.px, 26.px)
            .borderBottom(1.px, LineStyle.Solid, rgb(208, 224, 218))
    }
}

val DashboardTabCardTitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(15.px)
            .fontWeight(700)
            .color(Theme.DarkGreen.rgb)
            .margin(0.px)
    }
}

val DashboardTabCardSubtitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(12.px)
            .color(rgb(106, 144, 144))
            .margin(top = 3.px, bottom = 0.px, leftRight = 0.px)
    }
}

val DashboardTabCardBodyStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(20.px, 26.px)
    }
}

val AttestationAlertContainerStyle = CssStyle {
    base {
        Modifier
            .background(rgb(254, 253, 245))
            .color(rgb(122, 106, 21))
            .fontSize(12.px)
            .border(1.5.px, LineStyle.Solid, rgb(224, 216, 144))
            .borderRadius(10.px)
            .padding(18.px, 22.px)
            .margin(bottom = 1.cssRem)
    }
}

val AttestationAlertListStyle = CssStyle {
    base {
        Modifier
            .fontSize(12.px)
            .color(rgb(90, 85, 48))
            .lineHeight(1.6)
            .margin(bottom = 4.px)
    }
    cssRule("li p") {
        Modifier
            .margin(0.px)
    }
}

val AttestationAlertCheckboxStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(9.px)
            .cursor(Cursor.Pointer)
            .fontSize(13.px)
            .fontWeight(600)
            .color(rgb(61, 53, 16))
            .margin(top = 14.px)
    }
}

// Success Checkmark Circle
val SuccessCheckmarkCircleStyle = CssStyle {
    base {
        Modifier
            .width(64.px)
            .height(64.px)
            .borderRadius(50.percent)
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .margin(leftRight = autoLength, bottom = 16.px)
            .fontSize(30.px)
            .color(Colors.White)
            .background(Theme.DarkGreen.rgb)

    }
}